import { UcoastEl } from '@/scripts/core/UcoastEl'
import { TsDOM as q } from '@/scripts/core/TsDOM'

type NavState = 'default' | 'order' | 'discover'

const CLOSE_DELAY_MS = 150

export class FloatingNav extends UcoastEl {
	static htmlSelector = 'floating-nav'

	private leaveTimer?: number
	private isTouch = false
	private toggles: HTMLElement[] = []
	private panel?: HTMLElement
	private togglesEl?: HTMLElement
	private indicator?: HTMLElement
	private onDocClick?: (e: MouseEvent) => void
	private onKeydown?: (e: KeyboardEvent) => void
	private onResize?: () => void
	private onFocusIn?: (e: FocusEvent) => void
	private onFocusOut?: () => void
	private resizeRaf?: number
	private heightRaf?: number
	private blurTimer?: number

	override connectedCallback() {
		super.connectedCallback()
		this.isTouch = window.matchMedia('(hover: none)').matches
		this.toggles = Array.from(q.ol<HTMLElement>('[data-floating-nav-toggle]', this) ?? [])
		this.panel = q.os<HTMLElement>('.floating-nav__panel', this)
		this.togglesEl = q.os<HTMLElement>('.floating-nav__toggles', this)
		this.indicator = q.os<HTMLElement>('.floating-nav__toggle-indicator', this)

		this.bindToggles()
		this.bindContainerHover()
		this.bindIndicator()
		this.bindOutsideAndEscape()
		this.bindResize()
		this.bindKeyboard()
	}

	override disconnectedCallback() {
		super.disconnectedCallback()
		if (this.onDocClick) document.removeEventListener('click', this.onDocClick)
		if (this.onKeydown) document.removeEventListener('keydown', this.onKeydown)
		if (this.onResize) window.removeEventListener('resize', this.onResize)
		if (this.onFocusIn) document.removeEventListener('focusin', this.onFocusIn)
		if (this.onFocusOut) document.removeEventListener('focusout', this.onFocusOut)
		window.clearTimeout(this.leaveTimer)
		window.clearTimeout(this.blurTimer)
		window.cancelAnimationFrame(this.resizeRaf ?? 0)
		window.cancelAnimationFrame(this.heightRaf ?? 0)
	}

	private bindToggles() {
		this.toggles.forEach((toggle) => {
			const target = toggle.dataset.floatingNavToggle as NavState
			if (!target) return

			toggle.addEventListener('focus', () => this.setState(target))

			if (this.isTouch) {
				toggle.addEventListener('click', (e) => {
					e.preventDefault()
					this.setState(this.getState() === target ? 'default' : target)
				})
				return
			}

			toggle.addEventListener('mouseenter', () => {
				window.clearTimeout(this.leaveTimer)
				this.setState(target)
			})
			toggle.addEventListener('click', (e) => {
				e.preventDefault()
				this.setState(this.getState() === target ? 'default' : target)
			})
		})
	}

	private bindContainerHover() {
		if (this.isTouch) return
		this.addEventListener('mouseenter', () => window.clearTimeout(this.leaveTimer))
		this.addEventListener('mouseleave', () => {
			window.clearTimeout(this.leaveTimer)
			this.leaveTimer = window.setTimeout(() => this.setState('default'), CLOSE_DELAY_MS)
		})
	}

	// Sliding indicator that tracks the active/hovered toggle.
	private bindIndicator() {
		if (!this.indicator || !this.togglesEl) return
		if (!this.isTouch) {
			this.toggles.forEach((toggle) => {
				toggle.addEventListener('mouseenter', () => this.moveIndicator(toggle))
				toggle.addEventListener('focus', () => this.moveIndicator(toggle))
			})
			this.togglesEl.addEventListener('mouseleave', () => this.syncIndicator())
		}
		this.syncIndicator()
	}

	private moveIndicator(toggle: HTMLElement) {
		if (!this.indicator) return
		this.indicator.style.left = `${toggle.offsetLeft}px`
		this.indicator.style.width = `${toggle.offsetWidth}px`
		this.indicator.classList.add('is-visible')
	}

	private syncIndicator() {
		if (!this.indicator) return
		const state = this.getState()
		if (state === 'default') {
			this.indicator.classList.remove('is-visible')
			return
		}
		const active = this.toggles.find((t) => t.dataset.floatingNavToggle === state)
		if (active) this.moveIndicator(active)
	}

	private bindOutsideAndEscape() {
		this.onDocClick = (e: MouseEvent) => {
			if (this.getState() === 'default') return
			if (!this.contains(e.target as Node)) this.setState('default')
		}
		this.onKeydown = (e: KeyboardEvent) => {
			if (e.key === 'Escape' && this.getState() !== 'default') {
				this.setState('default')
				;(this.toggles[0] as HTMLElement | undefined)?.focus()
			}
		}
		document.addEventListener('click', this.onDocClick)
		document.addEventListener('keydown', this.onKeydown)
	}

	// Re-measure the open panel on resize (content reflows at different widths).
	private bindResize() {
		this.onResize = () => {
			window.cancelAnimationFrame(this.resizeRaf ?? 0)
			this.resizeRaf = window.requestAnimationFrame(() => {
				if (this.getState() !== 'default') this.setPanelHeight()
			})
		}
		window.addEventListener('resize', this.onResize)
	}

	// On touch, close the nav while an on-screen keyboard is up and flag it so
	// the bottom-anchored nav can get out of the way.
	private bindKeyboard() {
		if (!this.isTouch) return
		this.onFocusIn = (e: FocusEvent) => {
			if (!this.isEditable(e.target)) return
			window.clearTimeout(this.blurTimer)
			this.setState('default')
			this.dataset.keyboard = 'open'
		}
		this.onFocusOut = () => {
			window.clearTimeout(this.blurTimer)
			this.blurTimer = window.setTimeout(() => {
				if (!this.isEditable(document.activeElement)) delete this.dataset.keyboard
			}, CLOSE_DELAY_MS)
		}
		document.addEventListener('focusin', this.onFocusIn)
		document.addEventListener('focusout', this.onFocusOut)
	}

	private isEditable(target: EventTarget | null): boolean {
		if (!(target instanceof HTMLElement)) return false
		if (target.isContentEditable) return true
		const tag = target.tagName
		if (tag === 'TEXTAREA') return true
		if (tag !== 'INPUT') return false
		return !['button', 'submit', 'reset', 'checkbox', 'radio', 'range', 'color', 'file', 'image', 'hidden'].includes(
			(target as HTMLInputElement).type
		)
	}

	// Animate the panel between height:0 and its natural content height. The CSS
	// keeps the panel at height:0 by default; this measures scrollHeight and sets
	// an explicit pixel height so the `height` transition can run.
	private setPanelHeight() {
		if (!this.panel) return
		const panel = this.panel
		window.cancelAnimationFrame(this.heightRaf ?? 0)
		if (this.getState() === 'default') {
			panel.style.height = '0px'
			return
		}
		const current = panel.style.height || '0px'
		panel.style.height = 'auto'
		const target = panel.scrollHeight
		panel.style.height = current
		// Force reflow so the browser registers the pre-animation height.
		void panel.offsetHeight
		this.heightRaf = window.requestAnimationFrame(() => {
			panel.style.height = `${target}px`
		})
	}

	private getState(): NavState {
		return (this.dataset.state as NavState) || 'default'
	}

	private setState(state: NavState) {
		if (this.getState() === state) return
		this.dataset.state = state
		const isOpen = state !== 'default'
		this.toggles.forEach((toggle) => {
			const target = toggle.dataset.floatingNavToggle
			toggle.setAttribute('aria-expanded', String(isOpen && target === state))
		})
		this.panel?.setAttribute('aria-hidden', String(!isOpen))
		this.setPanelHeight()
		this.syncIndicator()
		if (isOpen) {
			document.dispatchEvent(new CustomEvent('floating-nav:open', { detail: { state } }))
		}
	}
}
